/**
 * @file SampleProductPicker.tsx
 * @description «Товар для примера ▾» над канвой шаблона страницы товара
 * (phase19.16 Р2а; uiux-design-r2a §3, §7): поиск по названию и быстрые
 * варианты — с несколькими фасовками, без фото, «скоро в продаже», без
 * отзывов, без описания (`GET /admin/site/templates/product/sample-products`).
 * Выбранный товар показывается на канве; шаблон от выбора не меняется.
 */

import { ChevronDown } from "lucide-react";
import { useEffect, useState } from "react";

import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { SearchInput } from "../../ui/SearchInput";
import { SAMPLE_FILTERS, SAMPLE_FILTER_LABELS, siteTemplates, type SampleFilter, type TemplateSampleItem, type TemplateSampleProduct } from "../../../lib/api-site-templates";
import { pluralRu } from "../../../lib/plural";
import { cn } from "@/lib/utils";

interface Props {
  /** Товар на канве сейчас. */
  current: TemplateSampleProduct | null;
  onPick: (product: TemplateSampleProduct) => void;
}

const SEARCH_DELAY_MS = 300;

/** «2 фасовки · скоро в продаже · без отзывов» — чем пример интересен. */
function sampleFacts(p: TemplateSampleItem): string {
  return [
    `${p.skuCount} ${pluralRu(p.skuCount, ["фасовка", "фасовки", "фасовок"])}`,
    p.imageUrl ? null : "без фото",
    p.purchasable ? null : "скоро в продаже",
    p.reviewsCount > 0 ? `отзывов ${p.reviewsCount}` : "без отзывов",
    p.hasDescription ? null : "без описания",
  ]
    .filter(Boolean)
    .join(" · ");
}

export function SampleProductPicker({ current, onPick }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<SampleFilter | null>(null);
  const [items, setItems] = useState<TemplateSampleItem[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    const t = setTimeout(() => {
      siteTemplates
        .sampleProducts({ q: query, filter })
        .then((list) => {
          if (!alive) return;
          setItems(list);
          setFailed(false);
        })
        .catch(() => alive && setFailed(true));
    }, SEARCH_DELAY_MS);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [open, query, filter]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={(props) => (
          <button
            {...props}
            type="button"
            className="flex min-h-12 w-full min-w-0 items-center gap-2 rounded-xl bg-surface-lowest px-3 text-left outline-none [box-shadow:inset_0_0_0_1px_var(--border)] focus-visible:ring-2 focus-visible:ring-ring md:w-auto md:max-w-80"
          >
            <span className="type-caption shrink-0 text-on-surface-variant">Товар для примера:</span>
            <span className="type-body min-w-0 flex-1 truncate font-medium text-on-surface">{current?.title ?? "выбрать"}</span>
            <ChevronDown aria-hidden className="size-4 shrink-0 text-on-surface-variant" />
          </button>
        )}
      />
      <PopoverContent align="start" className="flex max-h-[70dvh] w-96 max-w-[calc(100vw-2rem)] flex-col gap-2 overflow-y-auto p-2">
        <SearchInput value={query} onChange={setQuery} placeholder="Найти товар" ariaLabel="Поиск товара для примера" />
        <div className="flex flex-wrap gap-1" role="group" aria-label="Быстрый выбор">
          {SAMPLE_FILTERS.map((key) => (
            <button
              key={key}
              type="button"
              aria-pressed={filter === key}
              onClick={() => setFilter(filter === key ? null : key)}
              className={cn(
                "type-caption min-h-12 rounded-full px-3 outline-none focus-visible:ring-2 focus-visible:ring-ring",
                filter === key ? "bg-primary/15 font-medium text-on-surface" : "bg-surface-low text-on-surface-variant hover:bg-primary/5",
              )}
            >
              {SAMPLE_FILTER_LABELS[key]}
            </button>
          ))}
        </div>
        {failed ? <p className="type-caption p-2 text-on-surface-variant">Товары сейчас не загружаются — проверьте позже.</p> : null}
        {items && items.length === 0 ? <p className="type-caption p-2 text-on-surface-variant">Таких товаров на сайте нет.</p> : null}
        {(items ?? []).map((product) => (
          <button
            key={product.productId}
            type="button"
            onClick={() => {
              onPick({ productId: product.productId, slug: product.slug, title: product.title });
              setOpen(false);
            }}
            aria-current={current?.productId === product.productId ? "true" : undefined}
            className={cn("flex min-h-12 items-center gap-3 rounded-lg px-3 text-left", current?.productId === product.productId ? "bg-primary/10" : "hover:bg-surface-low")}
          >
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="type-body truncate text-on-surface">{product.title}</span>
              <span className="type-caption truncate text-on-surface-variant">{sampleFacts(product)}</span>
            </span>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}
